/**
 * De muur van helden (SPEC §6.2): de publieke voorpagina.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, metHerkansing, type Muur as Muurvorm, type Muurheld } from '../api';
import { Euro, Kroon, Laden, Melding, NIVEAUKLEUR, useTitel } from '../componenten/basis';
import { Pagina } from '../componenten/Schil';
import { NIVEAUNAMEN, NIVEAUS, type Niveau } from '../lib/niveau';
import { ECHTE_HELDEN, TEKSTEN } from '../lib/teksten';

export function Muur() {
  useTitel('');
  const [muur, zetMuur] = useState<Muurvorm | null>(null);
  const [fout, zetFout] = useState<string | null>(null);

  useEffect(() => {
    metHerkansing(() => api.haal<Muurvorm>('/api/muur'))
      .then(zetMuur)
      .catch((f: Error) => zetFout(f.message));
  }, []);

  return (
    <Pagina>
      <Held muur={muur} />
      <EchteHelden />
      {fout && <Melding soort="fout">{fout}</Melding>}
      {!muur && !fout && <Laden tekst="De heldenmuur wordt opgebouwd…" />}
      {muur && (
        <>
          <Niveaus muur={muur} />
          <Heldenmuur muur={muur} />
          <Bedrijven muur={muur} />
          <Slot muur={muur} />
        </>
      )}
    </Pagina>
  );
}

function Held({ muur }: { muur: Muurvorm | null }) {
  return (
    <section className="verschijn relative pt-8 pb-14 sm:pt-14 sm:pb-24">
      <div className="mx-auto max-w-3xl text-center">
        <span className="woordmerk mx-auto mt-2 block h-20 w-[11.4rem] text-[#222] sm:h-24 sm:w-[13.7rem]" role="img" aria-label="1e Hilversumsche Groep" />
        <p className="mt-5 text-xs font-semibold uppercase leading-relaxed tracking-[0.3em] text-groep-bruin">
          Scouting Hilfertheem
          <span className="block">Hilversum</span>
        </p>
        <h1 className="mt-8 text-5xl sm:text-7xl">
          <span className="text-groep-bruin">Helden</span> <span className="text-[#222]">van de</span> <span className="text-groep-groen">Hilfertheem</span>
        </h1>
        <p className="mt-4 text-sm text-groep-bruin">{TEKSTEN.afzender}</p>
        <p className="mx-auto mt-6 max-w-xl text-lg text-inkt-zacht">
          {TEKSTEN.intro}
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/word-held" className="knop knop-goud">
            Word held voor <Euro cent={muur?.tarieven.jaarbijdrage_cent ?? 10000} /> per jaar
          </Link>
          <a href="#muur" className="knop knop-stil">
            Bekijk de helden
          </a>
        </div>
        {muur && muur.aantal > 0 && (
          <dl className={`mx-auto mt-12 grid max-w-lg gap-4 text-center ${muur.oudsteJaren > 0 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <Cijfer getal={muur.aantal} woord={muur.aantal === 1 ? 'held' : 'helden'} />
            <Cijfer getal={muur.perNiveau.goud + muur.perNiveau.zilver} woord="zilver of goud" />
            {muur.oudsteJaren > 0 && <Cijfer getal={muur.oudsteJaren} woord="jaar trouwste held" />}
          </dl>
        )}
      </div>
    </section>
  );
}

/**
 * De échte helden: de vrijwilligers die de groep elke week draaiende houden.
 * Bewust anders dan de donateurs eromheen: een groen vlak, geen kronen.
 */
function EchteHelden() {
  const e = ECHTE_HELDEN;
  return (
    <section className="verschijn pb-14 sm:pb-24">
      <div className="relative overflow-hidden rounded-[1.75rem] border border-groep-groen/25 bg-gradient-to-b from-groen-zacht to-nacht px-6 py-10 text-center shadow-[0_30px_70px_-50px_rgb(23_102_58_/_0.45)] sm:px-12 sm:py-12">
        <div className="pointer-events-none absolute inset-x-[15%] top-0 h-px bg-gradient-to-r from-transparent via-groep-groen to-transparent" />
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-groep-bruin">Zonder deze helden geen Hilfertheem</p>
        <h2 className="mt-3 text-3xl sm:text-4xl">De échte helden</h2>
        <p className="mx-auto mt-3 max-w-xl text-inkt-zacht">
          {e.wie.map((regel) => (
            <span key={regel} className="block">
              {regel}
            </span>
          ))}
        </p>
        <dl className="mx-auto mt-8 grid max-w-3xl gap-6 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-groep-groen/20">
          <EchtGetal getal={e.vrijwilligers} woord="vrijwilligers" />
          <EchtGetal getal={e.jarenVrijwilligerswerk} woord="jaar vrijwilligerswerk" />
          <EchtGetal getal={e.jeugdleden} woord="jeugdleden, elk weekend" />
        </dl>
      </div>
    </section>
  );
}

function EchtGetal({ getal, woord }: { getal: number; woord: string }) {
  return (
    <div className="flex flex-col-reverse px-4">
      <dt className="mt-1 text-xs uppercase tracking-widest text-inkt-zacht">{woord}</dt>
      <dd className="font-display text-5xl text-groep-groen tabular-nums sm:text-6xl">{getal}</dd>
    </div>
  );
}

function Cijfer({ getal, woord }: { getal: number; woord: string }) {
  return (
    <div>
      <dt className="font-display text-4xl text-goud-300 tabular-nums sm:text-5xl">{getal}</dt>
      <dd className="mt-1 text-xs uppercase tracking-widest text-inkt-zacht">{woord}</dd>
    </div>
  );
}

function Niveaus({ muur }: { muur: Muurvorm }) {
  const zilverNa = muur.drempels.zilverNaJaren - 1;
  const goudNa = muur.drempels.goudNaJaren - 1;
  const uitleg: Record<Niveau, string> = {
    brons: 'Vanaf je eerste bijdrage. Je naam op de online heldenmuur, in de nieuwsbrief, en een uitnodiging voor de exclusieve jaarlijkse heldenborrel.',
    zilver: `Na ${zilverNa} volle jaren word je vanzelf zilver. Of meteen, voor wie niet wil wachten.`,
    goud: `Na ${goudNa} volle jaren word je vanzelf goud. De bovenste rij van de muur, een gouden naambordje (of je logo) achter de bar in het clubhuis, en onze eeuwige dank.`,
  };
  const prijs: Record<Niveau, number | null> = {
    brons: null,
    zilver: muur.afkoop.zilver_totaal_cent,
    goud: muur.afkoop.goud_totaal_cent,
  };
  const tarief = muur.tarieven.jaarbijdrage_cent;
  const jaren = (cent: number) => telwoord(Math.round(cent / tarief));
  return (
    <section className="pb-10 sm:pb-16">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl sm:text-4xl">Wat je ervoor terugkrijgt</h2>
        <ul className="mt-6 space-y-2 text-inkt-zacht">
          {TEKSTEN.wat_je_krijgt.map((regel) => (
            <li key={regel} className="flex items-start justify-center gap-2">
              <Kroon className="mt-1.5 h-3 w-3 shrink-0 text-goud-500" />
              <span>{regel}</span>
            </li>
          ))}
        </ul>
        <p className="mt-10 text-sm text-inkt-zacht">{TEKSTEN.waar_het_heen_gaat}</p>
      </div>
      <div className="mt-14 grid gap-4 sm:mt-24 sm:grid-cols-3">
        {NIVEAUS.map((n) => {
          const k = NIVEAUKLEUR[n];
          return (
            <div key={n} className={`kaart relative overflow-hidden p-6 ${n === 'goud' ? 'shadow-goud' : ''}`}>
              <div className={`absolute inset-x-0 top-0 h-1 ${n === 'goud' ? 'bg-goud' : n === 'zilver' ? 'bg-zilver' : 'bg-brons'}`} />
              <Kroon className={`h-7 w-7 ${k.tekst}`} />
              <h3 className={`mt-4 text-2xl ${k.tekst}`}>{NIVEAUNAMEN[n]}</h3>
              <p className="mt-2 text-sm text-inkt-zacht">{uitleg[n]}</p>
              {prijs[n] !== null && (
                <p className="mt-4 text-sm">
                  <span className="text-inkt-zacht">Meteen {NIVEAUNAMEN[n].toLowerCase()}: </span>
                  <Euro cent={prijs[n]!} /> <span className="text-inkt-zacht">nu ({jaren(prijs[n]!)} jaarbijdragen in één keer), vanaf volgend jaar weer <Euro cent={tarief} /> per jaar.</span>
                </p>
              )}
            </div>
          );
        })}
      </div>
      <p className="mx-auto mt-10 max-w-2xl text-center text-xs text-inkt-stil">
        Afkopen is geen vooruitbetaling: je bent meteen zilver of goud, maar de jaarbijdrage van <Euro cent={tarief} /> per kalenderjaar loopt vanaf het jaar erna gewoon door, net als bij iedere held. Je geeft dus extra, voor wie niet wil wachten. Meer geven mag altijd.
      </p>
    </section>
  );
}

function telwoord(n: number): string {
  return ['nul', 'één', 'twee', 'drie', 'vier', 'vijf', 'zes', 'zeven', 'acht', 'negen', 'tien'][n] ?? String(n);
}

function Heldenmuur({ muur }: { muur: Muurvorm }) {
  const personen = muur.helden.filter((h) => h.soort === 'persoon');
  const groepen = NIVEAUS.slice().reverse().map((n) => ({ niveau: n, helden: personen.filter((h) => h.niveau === n) }));
  return (
    <section id="muur" className="muurkader scroll-mt-8 my-10 px-5 py-8 sm:my-16 sm:px-10 sm:py-12">
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-3xl sm:text-4xl">De heldenmuur</h2>
        <p className="text-sm text-inkt-zacht">{muur.aantal === 0 ? 'Nog leeg - jij kunt de eerste zijn.' : `${muur.aantal} ${muur.aantal === 1 ? 'held' : 'helden'}`}</p>
      </div>
      {muur.aantal === 0 && (
        <div className="kaart mt-8 p-10 text-center">
          <Kroon className="mx-auto h-10 w-10 text-goud-600" />
          <p className="mt-4 text-inkt-zacht">De eerste naam op deze muur is nog niet geschreven.</p>
          <Link to="/word-held" className="knop knop-goud mt-6">
            Schrijf de jouwe
          </Link>
        </div>
      )}
      {groepen
        .filter((g) => g.helden.length > 0)
        .map((g) => (
          <div key={g.niveau} className="mt-10">
            <h3 className={`flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.25em] ${NIVEAUKLEUR[g.niveau].tekst}`}>
              <Kroon className="h-4 w-4" />
              {NIVEAUNAMEN[g.niveau]}
              <span className="h-px flex-1 bg-lijn" />
            </h3>
            <ul className={`mt-5 grid gap-3 ${g.niveau === 'goud' ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
              {g.helden.map((h, i) => (
                <Heldkaart key={`${h.weergavenaam}-${i}`} held={h} />
              ))}
            </ul>
          </div>
        ))}
    </section>
  );
}

function Heldkaart({ held }: { held: Muurheld }) {
  const k = NIVEAUKLEUR[held.niveau];
  const goud = held.niveau === 'goud';
  return (
    <li className={`kaart verschijn flex min-w-0 items-start gap-4 p-5 ${goud ? 'shadow-goud bg-vlak-hoog' : ''}`}>
      <span className={`mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${k.rand} ${k.vlak}`}>
        <Kroon className={`h-5 w-5 ${k.tekst}`} />
      </span>
      <div className="min-w-0 flex-1">
        <p className={`font-display text-xl leading-tight break-words ${held.anoniem ? 'italic text-inkt-zacht' : ''} ${goud ? 'text-2xl' : ''}`}>{held.weergavenaam}</p>
        {held.regel && <p className="mt-1 text-sm leading-snug text-inkt-zacht break-words">{held.regel}</p>}
        <p className={`mt-2 flex flex-wrap items-center gap-2 text-xs uppercase tracking-widest ${k.tekst}`}>
          <span>Held sinds {held.sinds}</span>
          {held.jubileum && <Jubileumtag jaren={held.jubileum} />}
        </p>
      </div>
    </li>
  );
}

function Jubileumtag({ jaren }: { jaren: number }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-goud-500/50 bg-goud-zacht px-2 py-0.5 text-[0.65rem] font-semibold tracking-wider text-goud-300">
      <Kroon className="h-2.5 w-2.5" />
      {jaren} jaar held
    </span>
  );
}

function Bedrijven({ muur }: { muur: Muurvorm }) {
  const bedrijven = muur.helden.filter((h) => h.soort === 'bedrijf');
  return (
    <section className="py-10 sm:py-16">
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-3xl sm:text-4xl">Bedrijven die de Hilfertheem steunen</h2>
        <Link to="/word-held" className="text-sm text-goud-300">
          Ook meedoen?
        </Link>
      </div>
      <p className="mt-3 max-w-2xl text-inkt-zacht">{TEKSTEN.bedrijven}</p>
      {bedrijven.length > 0 && (
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {bedrijven.map((b, i) => {
            const k = NIVEAUKLEUR[b.niveau];
            const inhoud = (
              <>
                {b.logo_url ? (
                  <span className="flex h-24 items-center justify-center rounded-lg bg-white p-3">
                    <img src={b.logo_url} alt={b.weergavenaam} className="max-h-full max-w-full object-contain" loading="lazy" />
                  </span>
                ) : (
                  <span className={`flex h-24 items-center justify-center rounded-lg ${k.vlak}`}>
                    <Kroon className={`h-8 w-8 ${k.tekst}`} />
                  </span>
                )}
                <p className={`mt-4 font-display text-xl ${b.anoniem ? 'italic text-inkt-zacht' : ''}`}>{b.weergavenaam}</p>
                {b.regel && <p className="mt-1 text-sm text-inkt-zacht">{b.regel}</p>}
                <p className={`mt-2 flex flex-wrap items-center gap-2 text-xs uppercase tracking-widest ${k.tekst}`}>
                  <span>{NIVEAUNAMEN[b.niveau]} · sinds {b.sinds}</span>
                  {b.jubileum && <Jubileumtag jaren={b.jubileum} />}
                </p>
              </>
            );
            return (
              <li key={`${b.weergavenaam}-${i}`} className={`kaart verschijn min-w-0 break-words p-5 ${b.niveau === 'goud' ? 'shadow-goud bg-vlak-hoog' : ''}`}>
                {b.website ? (
                  <a href={b.website} target="_blank" rel="noreferrer" className="block text-inkt no-underline">
                    {inhoud}
                  </a>
                ) : (
                  inhoud
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Slot({ muur }: { muur: Muurvorm }) {
  return (
    <section className="kaart mt-12 bg-gradient-to-br from-vlak-hoog to-vlak p-8 text-center sm:mt-16 sm:p-12">
      <h2 className="text-3xl sm:text-4xl">Jouw naam hier ook?</h2>
      <p className="mx-auto mt-3 max-w-md text-inkt-zacht">
        Eén keer aanmelden, daarna vanaf <Euro cent={muur.tarieven.jaarbijdrage_cent} /> per jaar via automatische incasso. Opzeggen kan altijd.
      </p>
      <Link to="/word-held" className="knop knop-goud mt-6">
        Word held
      </Link>
    </section>
  );
}
